import type { TRule } from 'fela';
import { round } from 'lodash-es';

import colors from './colors';

const breakpoints = {
    xs: 480,
    sm: 768,
    md: 1024,
    lg: 1440,
};

const zIndexes = {
    cta: '1000',
    mobileMenu: '999',
    mobileMenuOverlay: '998',
    updateFormButtons: '997',
};

const widths = {
    menu: 280,
};

const typography = {
    Headline1: {
        fontSize: '1.75rem',
        fontWeight: 700,
        lineHeight: '2rem',
    },
    Headline2: {
        fontSize: '1.25rem',
        fontWeight: 700,
        lineHeight: '1.625rem',
    },
    Headline3: {
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: '1.5rem',
    },
    ParagraphNormalMedium: {
        fontSize: '1rem',
        fontWeight: 470,
        lineHeight: '1.5rem',
    },
    ParagraphNormalRegular: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: '1.5rem',
    },
    ParagraphNormalBold: {
        fontSize: '1rem',
        fontWeight: 700,
        lineHeight: '1.5rem',
    },
    ParagraphSmallMedium: {
        fontSize: '0.875rem',
        fontWeight: 470,
        lineHeight: '1.125rem',
    },
    ParagraphSmallBold: {
        fontSize: '0.875rem',
        fontWeight: 700,
        lineHeight: '1rem',
    },
    ParagraphExtraSmallMedium: {
        fontSize: '0.625rem',
        fontWeight: 500,
        lineHeight: '0.75rem',
    },
    Password: {
        fontSize: '1.125rem',
        fontWeight: 400,
        lineHeight: '1.5rem',
        letterSpacing: '0.125rem',
    },
    ShowPassword: {
        fontFamily: 'DM Mono, monospace',
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: '1.5rem',
        letterSpacing: '0.01875rem',
    },
    MenuDefault: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '0.875rem',
        letterSpacing: '0.03125rem',
    },
    MenuUnderlined: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '0.875rem',
        letterSpacing: '0.03125rem',
        textDecoration: 'underline',
    },
    LinkSmall: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: '1rem',
    },
    LinkMedium: {
        fontSize: '0.875rem',
        fontWeight: 400,
        lineHeight: '1.25rem',
    },
    LinkLarge: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: '1.5rem',
    },
    LabelSmall: {
        fontSize: '0.625rem',
        fontWeight: 400,
        lineHeight: '0.75rem',
    },
    LabelLarge: {
        fontSize: '0.75rem',
        fontWeight: 400,
        lineHeight: '1rem',
    },
    ButtonNormal: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '1.5rem',
    },
    ButtonSmall: {
        fontSize: '0.875rem',
        fontWeight: 500,
        lineHeight: '1rem',
    },
    AlphabetSmall: {
        fontSize: '0.625rem',
        fontWeight: 400,
        lineHeight: '0.625rem',
    },
    AlphabetBig: {
        fontSize: '1.5rem',
        fontWeight: 400,
        lineHeight: '1.5rem',
        letterSpacing: '0.065625rem',
    },
};

const theme = {
    fontFamily: "'Google Sans', sans-serif",
    colors,
    breakpoints,
    zIndexes,
    widths,
    typography,
    spacing: (value: number) => `${round(value / 16, 2)}rem`,
};

export default theme;

export type Theme = typeof theme;

export type ThemeProps = { theme: Theme };

export type TRuleWithTheme<Props = {}> = TRule<ThemeProps & Props>;

export type RulesExtend<TExtandalbeRules, TProps = {}> = Partial<Record<keyof TExtandalbeRules, TRuleWithTheme<TProps>>>;
